import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { BrandingProvider } from "@/context/BrandingContext";
import Icon from "@/components/Icon";
import ImpersonationBanner from "@/components/ImpersonationBanner";

import Landing from "@/pages/Landing";
import Pricing from "@/pages/Pricing";
import Examples from "@/pages/Examples";
import Help from "@/pages/Help";
import Contact from "@/pages/Contact";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import TermsOfService from "@/pages/legal/TermsOfService";
import PublicLanding from "@/pages/PublicLanding";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import AdminBlog from "@/pages/admin/AdminBlog";
import AdminBlogComments from "@/pages/admin/AdminBlogComments";
import AuthCallback from "@/pages/AuthCallback";
import { Login, Register, ForgotPassword, ResetPassword } from "@/pages/Auth";

import Dashboard from "@/pages/app/Dashboard";
import Builder from "@/pages/app/Builder";
import Analytics from "@/pages/app/Analytics";
import Users from "@/pages/app/Users";
import Settings from "@/pages/app/Settings";
import Billing from "@/pages/app/Billing";
import PlanUsage from "@/pages/app/PlanUsage";
import CustomDomain from "@/pages/app/CustomDomain";
import AdminDomains from "@/pages/admin/AdminDomains";
import { IS_CUSTOM_HOST, MAIN_BACKEND_URL } from "@/lib/api";
import AcceptInvite from "@/pages/AcceptInvite";
import Templates from "@/pages/app/Templates";
import BulkCreator from "@/pages/app/BulkCreator";
import HelpApp from "@/pages/app/HelpApp";
import PaymentReturn from "@/pages/app/PaymentReturn";
import MyProfile from "@/pages/app/MyProfile";
import MyQRCodes from "@/pages/app/MyQRCodes";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminCustomerDetail from "@/pages/admin/AdminCustomerDetail";
import AdminAdministrators from "@/pages/admin/AdminAdministrators";
import AdminQRCodes from "@/pages/admin/AdminQRCodes";
import AdminPlans from "@/pages/admin/AdminPlans";
import AdminBilling from "@/pages/admin/AdminBilling";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import AdminGateways from "@/pages/admin/AdminGateways";
import AdminAuditLogs from "@/pages/admin/AdminAuditLogs";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminEmailPreview from "@/pages/admin/AdminEmailPreview";
import AdminEmailSettings from "@/pages/admin/AdminEmailSettings";
import AdminBranding from "@/pages/admin/AdminBranding";
import AdminCMS from "@/pages/admin/AdminCMS";
import AdminMedia from "@/pages/admin/AdminMedia";
import AdminSystem from "@/pages/admin/AdminSystem";
import AdminSupport from "@/pages/admin/AdminSupport";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminProfileFields from "@/pages/admin/AdminProfileFields";
import AdminQRTypes from "@/pages/admin/AdminQRTypes";
import AdminMenus from "@/pages/admin/AdminMenus";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="min-h-screen grid place-items-center bg-background"><Icon name="Loader2" className="h-8 w-8 animate-spin text-primary" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function GuestOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="min-h-screen grid place-items-center bg-background"><Icon name="Loader2" className="h-8 w-8 animate-spin text-primary" /></div>;
  if (user) return <Navigate to="/app" replace />;
  return children;
}

function Router() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      <ImpersonationBanner />
      <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/examples" element={<Examples />} />
      <Route path="/help" element={<Help />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/p/:publicId" element={<PublicLanding />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
      <Route path="/register" element={<GuestOnly><Register /></GuestOnly>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/app" element={<Protected><Dashboard /></Protected>} />
      <Route path="/app/builder" element={<Protected><Builder /></Protected>} />
      <Route path="/app/builder/:id" element={<Protected><Builder /></Protected>} />
      <Route path="/app/qr-codes" element={<Protected><MyQRCodes /></Protected>} />
      <Route path="/app/bulk" element={<Protected><BulkCreator /></Protected>} />
      <Route path="/app/templates" element={<Protected><Templates /></Protected>} />
      <Route path="/app/analytics" element={<Protected><Analytics /></Protected>} />
      <Route path="/app/analytics/:id" element={<Protected><Analytics /></Protected>} />
      <Route path="/app/users" element={<Protected><Users /></Protected>} />
      <Route path="/app/settings" element={<Protected><Settings /></Protected>} />
      <Route path="/app/profile" element={<Protected><MyProfile /></Protected>} />
      <Route path="/app/billing" element={<Protected><Billing /></Protected>} />
      <Route path="/app/plan-usage" element={<Protected><PlanUsage /></Protected>} />
      <Route path="/app/domain" element={<Protected><CustomDomain /></Protected>} />
      <Route path="/invite/:token" element={<AcceptInvite />} />
      <Route path="/payment/success" element={<Protected><PaymentReturn mode="success" /></Protected>} />
      <Route path="/payment/cancel" element={<Protected><PaymentReturn mode="cancel" /></Protected>} />
      <Route path="/app/help" element={<Protected><HelpApp /></Protected>} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminOverview />} />
      <Route path="/admin/customers" element={<AdminCustomers />} />
      <Route path="/admin/analytics" element={<AdminAnalytics />} />
      <Route path="/admin/blog" element={<AdminBlog />} />
      <Route path="/admin/blog-comments" element={<AdminBlogComments />} />
      <Route path="/admin/profile-fields" element={<AdminProfileFields />} />
      <Route path="/admin/customers/:id" element={<AdminCustomerDetail />} />
      <Route path="/admin/administrators" element={<AdminAdministrators />} />
      <Route path="/admin/qrcodes" element={<AdminQRCodes />} />
      <Route path="/admin/qr-types" element={<AdminQRTypes />} />
      <Route path="/admin/menus" element={<AdminMenus />} />
      <Route path="/admin/plans" element={<AdminPlans />} />
      <Route path="/admin/domains" element={<AdminDomains />} />
      <Route path="/admin/billing" element={<AdminBilling />} />
      <Route path="/admin/coupons" element={<AdminCoupons />} />
      <Route path="/admin/gateways" element={<AdminGateways />} />
      <Route path="/admin/audit-logs" element={<AdminAuditLogs />} />
      <Route path="/admin/settings" element={<AdminSettings />} />
      <Route path="/admin/email-preview" element={<AdminEmailPreview />} />
      <Route path="/admin/email-settings" element={<AdminEmailSettings />} />
      <Route path="/admin/branding" element={<AdminBranding />} />
      <Route path="/admin/cms" element={<AdminCMS />} />
      <Route path="/admin/media" element={<AdminMedia />} />
      <Route path="/admin/qr-controls" element={<AdminSystem />} />
      <Route path="/admin/system" element={<AdminSystem />} />
      <Route path="/admin/support" element={<AdminSupport />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

export default function App() {
  // Customer custom domains only serve public QR pages; everything else lives on the main domain.
  if (IS_CUSTOM_HOST && !window.location.pathname.startsWith("/p/")) {
    window.location.replace(MAIN_BACKEND_URL + (window.location.pathname === "/" ? "" : window.location.pathname));
    return null;
  }
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrandingProvider>
        <BrowserRouter>
          <Router />
          <Toaster position="top-center" richColors closeButton />
        </BrowserRouter>
        </BrandingProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
